import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  ViewEncapsulation,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Output,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogHeaderComponent, DialogFooterComponent, ButtonComponent } from '@akostm/orbit-ui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { User, UserRole } from '@clientwire/api';

export interface UserDialogData {
  title?: string;
  user?: User;
  cancelLabel?: string;
  confirmLabel?: string;
  message?: string;
  isConfirmation?: boolean;
}

@Component({
  selector: 'lib-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogHeaderComponent,
    DialogFooterComponent,
    ButtonComponent,
    ReactiveFormsModule,
    TranslateModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './user-dialog.component.html',
  styleUrl: './user-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDialogComponent implements OnInit {
  data: UserDialogData = inject(DIALOG_DATA);
  readonly #translateService = inject(TranslateService);
  readonly #dialogRef = inject(DialogRef);
  readonly #formBuilder = inject(FormBuilder);
  readonly #cdr = inject(ChangeDetectorRef);

  @Output() submitForm = new EventEmitter<any>();

  userForm!: FormGroup;
  roles = [UserRole.Owner, UserRole.User];
  isEditMode = false;
  isConfirmation = false;
  loading = false;
  errorMessage: string | null = null;

  ngOnInit(): void {
    this.isConfirmation = !!this.data.isConfirmation || !!this.data.message;

    if (!this.isConfirmation) {
      this.isEditMode = !!this.data.user;

      this.data.title =
        this.data.title ||
        this.#translateService.instant(
          this.isEditMode ? 'SETTINGS.USERS.EDIT_USER' : 'SETTINGS.USERS.ADD_USER'
        );

      this.data.cancelLabel =
        this.data.cancelLabel ||
        this.#translateService.instant('SHARED.DIALOGS.CONFIRM_DIALOG.CANCEL_LABEL');

      this.data.confirmLabel =
        this.data.confirmLabel ||
        this.#translateService.instant(
          this.isEditMode ? 'SETTINGS.USERS.SAVE_USER' : 'SETTINGS.USERS.ADD_USER'
        );

      this.initForm();
    } else {
      this.data.cancelLabel =
        this.data.cancelLabel ||
        this.#translateService.instant('SHARED.DIALOGS.CONFIRM_DIALOG.CANCEL_LABEL');

      this.data.confirmLabel =
        this.data.confirmLabel ||
        this.#translateService.instant('SHARED.DIALOGS.CONFIRM_DIALOG.CONFIRM_LABEL');
    }
  }

  initForm(): void {
    // Create form with different configurations based on edit mode
    if (this.isEditMode) {
      // When editing, don't include password field
      this.userForm = this.#formBuilder.group({
        email: [this.data.user?.email || '', [Validators.required, Validators.email]],
        firstname: [
          this.data.user?.firstname || '',
          [Validators.required, Validators.minLength(1)],
        ],
        lastname: [this.data.user?.lastname || '', [Validators.required, Validators.minLength(1)]],
        role: [this.data.user?.role || UserRole.User],
      });
    } else {
      // When creating a new user, include password field with validation
      this.userForm = this.#formBuilder.group({
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(8)]],
        firstname: ['', [Validators.required, Validators.minLength(1)]],
        lastname: ['', [Validators.required, Validators.minLength(1)]],
        role: [UserRole.User],
      });
    }
  }

  closeDialog(save: boolean): void {
    if (this.isConfirmation) {
      this.#dialogRef.close(save);
      return;
    }

    if (!save) {
      this.#dialogRef.close(false);
      return;
    }

    if (this.userForm.valid && !this.loading) {
      this.loading = true;
      this.errorMessage = null;
      this.#cdr.detectChanges();
      this.submitForm.emit(this.userForm.value);
    }
  }

  setError(message: string) {
    this.errorMessage = message;
    this.loading = false;
    this.#cdr.markForCheck();
    this.#cdr.detectChanges();
  }

  setLoading(isLoading: boolean) {
    this.loading = isLoading;
    if (isLoading) {
      this.errorMessage = null;
    }
    this.#cdr.markForCheck();
    this.#cdr.detectChanges();
  }
}
